import type { EmbedWidgetProps } from '@/embed/types';
import { OpportunityActions } from '@/embed/widgets/OpportunityActions';
import { OpportunityContract } from '@/embed/widgets/OpportunityContract';
import { OpportunityProposal } from '@/embed/widgets/OpportunityProposal';
import { ContactActions } from '@/embed/widgets/ContactActions';
import { AccountActions } from '@/embed/widgets/AccountActions';
import { OutreachWidget } from '@/embed/widgets/OutreachWidget';

/** One embeddable widget: its Salesforce-facing title and the component to render. */
export interface EmbedWidget {
  /** Human label for the widget (used as the section title in the embed). */
  readonly title: string;
  /** The React component, which receives the record context. */
  readonly Component: React.ComponentType<EmbedWidgetProps>;
}

/**
 * The embed component registry — the `componentName` a Salesforce placement
 * passes maps to the widget rendered. This lookup table is the seam every new
 * "expose X in Salesforce" surface plugs into: add a widget, add an entry, add
 * a placement. No control-flow edits.
 */
export const EMBED_REGISTRY: Readonly<Record<string, EmbedWidget>> = {
  'opportunity-actions': { title: 'Opportunity', Component: OpportunityActions },
  // The full contract workflow, for a dedicated (tall) Opportunity tab.
  'opportunity-contract': { title: 'Contract', Component: OpportunityContract },
  // The proposal generator, for a dedicated (tall) Opportunity tab. The API is
  // permission-gated (generate_proposals), so an unentitled user gets a 403.
  'opportunity-proposal': { title: 'Proposal', Component: OpportunityProposal },
  'contact-actions': { title: 'Contact', Component: ContactActions },
  'account-actions': { title: 'Account', Component: AccountActions },
  // Not record-bound — surfaced on a Salesforce App/Home page or tab.
  outreach: { title: 'Reach-out', Component: OutreachWidget },
};
